import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trophy, Trash2, Play, Calendar, Users, GitBranch, LayoutGrid, Eye, Search, Share2, Copy, CheckCircle, LogIn, LogOut, Crown } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useTournament } from "../context/TournamentContext";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import LanguageSelector from "../components/LanguageSelector";
import { useAuth } from "../context/AuthContext";

export default function HomePage() {
  const navigate = useNavigate();
  const { tournaments, deleteTournament, loadTournament, findTournamentByCode } = useTournament();
  const { t } = useLanguage();
  const { user, isAuthenticated, logout } = useAuth();

  const handleCreateClick = () => {
    if (isAuthenticated) {
      navigate("/setup");
    } else {
      toast.info(t("auth.signInRequired"));
      navigate("/login", { state: { from: "/setup" } });
    }
  };

  // View tournament form
  const [viewCode, setViewCode] = useState("");

  // Share dialog
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareTournament, setShareTournament] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const getShareLink = (tournamentId) => {
    return typeof window !== 'undefined' ? `${window.location.origin}/t/${tournamentId}` : '';
  };

  const handleShareClick = (tournament, e) => {
    e.stopPropagation();
    setShareTournament(tournament);
    setShareDialogOpen(true);
    setLinkCopied(false);
  };

  const copyShareLink = async () => {
    if (!shareTournament) return;
    try {
      await navigator.clipboard.writeText(getShareLink(shareTournament.id));
      setLinkCopied(true);
      toast.success(t('toast.linkCopied'));
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      toast.error(t('toast.linkCopyFailed'));
    }
  };

  const handleResumeTournament = (tournament) => {
    loadTournament(tournament.id);
    if (tournament.status === "completed") {
      navigate(`/champion/${tournament.id}`);
    } else if (tournament.format === "league") {
      navigate(`/league/${tournament.id}`);
    } else {
      navigate(`/bracket/${tournament.id}`);
    }
  };

  const handleViewTournament = async () => {
    if (!viewCode.trim()) {
      toast.error(t('toast.enterCode'));
      return;
    }

    toast.loading(t('loading'));
    const tournament = await findTournamentByCode(viewCode.trim());
    toast.dismiss();
    
    if (tournament) {
      toast.success(t('toast.tournamentFound'));
      if (tournament.status === "completed") {
        navigate(`/champion/${tournament.id}`);
      } else if (tournament.format === "league") {
        navigate(`/league/${tournament.id}`);
      } else {
        navigate(`/bracket/${tournament.id}`);
      }
    } else {
      toast.error(t('toast.tournamentNotFound'));
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-neon-green/20 text-neon-green border-neon-green/30">{t('home.completed')}</Badge>;
      case "active":
        return <Badge className="bg-neon-cyan/20 text-neon-cyan border-neon-cyan/30">In Progress</Badge>;
      default:
        return <Badge className="bg-white/10 text-white/60 border-white/20">Not Started</Badge>;
    }
  };

  const getFormatIcon = (format) => {
    return format === "league" ? (
      <LayoutGrid className="w-4 h-4" />
    ) : (
      <GitBranch className="w-4 h-4" />
    );
  };

  return (
    <div className="min-h-screen hero-gradient">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/30 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-neon-cyan/20 rounded flex items-center justify-center">
                <Trophy className="w-6 h-6 text-neon-cyan" />
              </div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-white uppercase">
                {t('appName')}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSelector />
              {isAuthenticated && user ? (
                <div className="flex items-center gap-2" data-testid="user-menu">
                  <button
                    onClick={() => navigate("/profile")}
                    data-testid="profile-link-btn"
                    className="flex items-center gap-2 px-3 py-1.5 rounded bg-white/5 border border-white/10 hover:border-neon-cyan/40 transition-colors text-left"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-neon-cyan/20 flex items-center justify-center text-neon-cyan text-xs font-bold uppercase flex-shrink-0">
                        {(user.name || user.email || "?").charAt(0)}
                      </div>
                    )}
                    <span className="flex flex-col leading-tight">
                      <span className="text-white/80 text-sm max-w-[140px] truncate hidden sm:inline" data-testid="user-name">
                        {user.name || user.email}
                      </span>
                      {user.is_owner ? (
                        <span
                          data-testid="owner-badge"
                          className="inline-flex items-center gap-0.5 text-yellow-400 text-xs font-bold uppercase tracking-wide"
                        >
                          <Crown className="w-3 h-3" /> {t("auth.owner")}
                        </span>
                      ) : user.verified ? (
                        <span
                          data-testid="verified-badge"
                          className="inline-flex items-center gap-0.5 text-green-400 text-xs font-semibold"
                        >
                          {`✓ ${t("auth.verified")}`}
                        </span>
                      ) : (
                        <span
                          data-testid="not-verified-badge"
                          className="inline-flex items-center gap-0.5 text-red-400 text-xs font-semibold"
                        >
                          {`✗ ${t("auth.notVerified")}`}
                        </span>
                      )}
                    </span>
                  </button>
                  <Button
                    data-testid="logout-btn"
                    onClick={logout}
                    variant="ghost"
                    size="sm"
                    className="text-white/60 hover:text-white hover:bg-white/10"
                  >
                    <LogOut className="w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">{t("auth.signOut")}</span>
                  </Button>
                </div>
              ) : (
                <Button
                  data-testid="header-signin-btn"
                  onClick={() => navigate("/login")}
                  className="bg-neon-cyan/20 hover:bg-neon-cyan/30 text-neon-cyan border border-neon-cyan/30 font-heading uppercase tracking-widest text-sm"
                >
                  <LogIn className="w-4 h-4 mr-1" />
                  {t("auth.signIn")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl md:text-6xl font-black uppercase tracking-tight mb-4 text-white">
              {t('home.title')}
            </h2>
            <p className="text-white/60 text-lg max-w-xl mx-auto mb-8">
              {t('home.subtitle')}
            </p>
          </div>

          {/* Action Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* Create Tournament */}
            <div className="card-surface p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-neon-cyan/20 rounded flex items-center justify-center">
                  <Plus className="w-5 h-5 text-neon-cyan" />
                </div>
                <h3 className="font-heading text-xl uppercase text-white">{t('home.createNew')}</h3>
              </div>
              <p className="text-white/50 text-sm mb-4">
                {t('home.createTournamentDesc')}
              </p>
              <Button
                data-testid="create-tournament-btn"
                onClick={handleCreateClick}
                className="w-full btn-skewed bg-neon-cyan hover:bg-neon-cyan/90 text-black font-heading font-bold uppercase tracking-widest py-4 hover:shadow-neon transition-all duration-300"
              >
                <span className="flex items-center justify-center gap-2">
                  <Plus className="w-5 h-5" />
                  {t('home.getStarted')}
                </span>
              </Button>
            </div>

            {/* View Tournament */}
            <div className="card-surface p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-neon-purple/20 rounded flex items-center justify-center">
                  <Eye className="w-5 h-5 text-neon-purple" />
                </div>
                <h3 className="font-heading text-xl uppercase text-white">{t('home.viewTournament')}</h3>
              </div>
              <p className="text-white/50 text-sm mb-4">
                {t('home.viewTournamentDesc')}
              </p>
              <div className="space-y-3">
                <Input
                  data-testid="view-tournament-code"
                  value={viewCode}
                  onChange={(e) => setViewCode(e.target.value.toUpperCase())}
                  placeholder={t('home.enterCode')}
                  className="input-dark uppercase"
                  maxLength={10}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleViewTournament(); }}
                />
                <Button
                  data-testid="view-tournament-btn"
                  onClick={handleViewTournament}
                  className="w-full bg-neon-purple/20 hover:bg-neon-purple/30 text-neon-purple border border-neon-purple/30 font-heading uppercase tracking-widest py-4"
                >
                  <span className="flex items-center justify-center gap-2">
                    <Search className="w-5 h-5" />
                    {t('home.viewBtn')}
                  </span>
                </Button>
              </div>
            </div>
          </div>

          {/* Dashboard */}
          {tournaments.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-6">
                <Calendar className="w-5 h-5 text-white/40" />
                <h3 className="font-heading text-xl uppercase tracking-wide text-white/80">
                  {t('home.yourTournaments')} ({tournaments.length})
                </h3>
              </div>

              <div className="grid gap-4">
                {tournaments.map((tournament) => (
                  <div
                    key={tournament.id}
                    className="card-surface p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 group"
                    data-testid={`tournament-card-${tournament.id}`}
                  >
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div
                        className={`w-12 h-12 rounded flex items-center justify-center ${
                          tournament.status === "completed"
                            ? "bg-neon-green/20"
                            : "bg-neon-cyan/10"
                        }`}
                      >
                        <Trophy
                          className={`w-6 h-6 ${
                            tournament.status === "completed"
                              ? "text-neon-green"
                              : "text-neon-cyan/60"
                          }`}
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mb-1">
                          <h4 className="font-heading text-lg uppercase font-bold text-white break-words">
                            {tournament.name}
                          </h4>
                          <Badge className="bg-white/10 text-white/60 border-white/20 font-data text-xs">
                            {tournament.code}
                          </Badge>
                          {getStatusBadge(tournament.status)}
                        </div>
                        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-sm">
                          <span className="text-white/40 font-data flex items-center gap-1">
                            {getFormatIcon(tournament.format)}
                            {tournament.format === "league" ? "League" : "Bracket"}
                            {tournament.hasLosersLeague && " + Losers"}
                          </span>
                          <span className="text-white/20">|</span>
                          <span className="text-white/40 font-data">{tournament.sportType}</span>
                          <span className="text-white/20">|</span>
                          <span className="text-white/40 font-data flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {tournament.participants.length}
                          </span>
                          {tournament.champion && (
                            <>
                              <span className="text-white/20">|</span>
                              <span className="text-neon-green font-data">
                                {t('bracket.champion')}: {tournament.champion?.name}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
                      <Button
                        data-testid={`share-tournament-${tournament.id}`}
                        onClick={(e) => handleShareClick(tournament, e)}
                        variant="ghost"
                        size="icon"
                        className="text-white/40 hover:text-neon-cyan hover:bg-neon-cyan/10"
                        title={t('share')}
                      >
                        <Share2 className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`resume-tournament-${tournament.id}`}
                        onClick={() => handleResumeTournament(tournament)}
                        variant="ghost"
                        className="text-neon-cyan hover:bg-neon-cyan/10 hover:text-neon-cyan"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        {tournament.status === "completed" ? t('view') : t('home.resume')}
                      </Button>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            data-testid={`delete-tournament-${tournament.id}`}
                            variant="ghost"
                            size="icon"
                            className="text-white/40 hover:text-red-500 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-surface-card border-white/10">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-heading uppercase text-white">
                              {t('home.deleteTournament')}
                            </AlertDialogTitle>
                            <AlertDialogDescription className="text-white/60">
                              {t('home.deleteConfirm')}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-white/10 text-white border-white/20 hover:bg-white/20">
                              {t('cancel')}
                            </AlertDialogCancel>
                            <AlertDialogAction
                              data-testid={`confirm-delete-${tournament.id}`}
                              onClick={() => deleteTournament(tournament.id)}
                              className="bg-red-500 text-white hover:bg-red-600"
                            >
                              {t('delete')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {tournaments.length === 0 && (
            <div className="text-center py-12 border border-dashed border-white/10 rounded">
              <Trophy className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="text-white/40 text-lg">{t('home.noTournaments')}</p>
            </div>
          )}
        </div>
      </main>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="bg-surface-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase text-white flex items-center gap-2">
              <Share2 className="w-5 h-5 text-neon-cyan" />
              {t('shareDialog.title')}
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {t('shareDialog.description')}
            </DialogDescription>
          </DialogHeader>

          {shareTournament && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-white/60 text-sm font-heading uppercase">{t('shareDialog.shareLink')}</label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={getShareLink(shareTournament.id)}
                    className="input-dark font-data text-sm"
                    data-testid="share-link-input"
                  />
                  <Button
                    onClick={copyShareLink}
                    className={`px-4 ${linkCopied ? 'bg-neon-green text-black' : 'bg-neon-cyan text-black'} hover:opacity-90`}
                    data-testid="copy-link-btn"
                  >
                    {linkCopied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* QR Code */}
              <div className="flex justify-center py-4">
                <div className="bg-white p-3 rounded-lg">
                  <QRCodeSVG 
                    value={getShareLink(shareTournament.id)} 
                    size={150}
                    level="M"
                    data-testid="share-qr-code"
                  />
                </div>
              </div>

              <div className="border-t border-white/10 pt-4">
                <p className="text-white/40 text-xs">
                  {t('shareDialog.orShare')}
                </p>
                <div className="flex gap-4 mt-2">
                  <div>
                    <span className="text-white/60 text-xs">{t('shareDialog.name')}:</span>
                    <p className="text-white font-heading uppercase">{shareTournament.name}</p>
                  </div>
                  <div>
                    <span className="text-white/60 text-xs">{t('shareDialog.code')}:</span>
                    <p className="text-neon-cyan font-data font-bold">{shareTournament.code}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={() => setShareDialogOpen(false)}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              {t('close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-auto">
        <div className="container mx-auto px-6 py-6 text-center">
          <p className="text-white/30 text-sm font-data">
            {t('appName')} — {t('tagline')}
          </p>
        </div>
      </footer>
    </div>
  );
}
